import { useState } from "react";
import { useAnalyticsQuery } from "@/hooks/useAnalyticsQuery";
import { analyticsApi } from "@/services/apiClient";
import { MAX_RANGE_DAYS, daysAgoISODate, todayISODate } from "@/stores/filterStore";
import SectionRangePicker from "@/components/SectionRangePicker";
import InfoTooltip from "@/components/InfoTooltip";
import KpiCard, { KpiRow } from "@/components/KpiCard";
import { fmt } from "@/lib/format";
import { balanceStatus, isInternalAccount } from "@/lib/thresholds";
import { formatBucketLabel } from "@/lib/timeseries";
import Panel from "./Panel";
import Skeleton from "./Skeleton";

function defaultRange() {
  return { from: daysAgoISODate(MAX_RANGE_DAYS - 1), to: todayISODate() };
}

const AT_RISK_DAYS = 7;

function heatColor(pct) {
  if (!pct || pct <= 0) return undefined;
  const lightness = Math.round(95 - pct * 27);
  return `hsl(158 52% ${lightness}%)`;
}

// 섹션 라벨 — 제목 + 부제
function StepLabel({ title, desc }) {
  return (
    <div className="mb-3 flex items-baseline gap-2">
      <span className="text-[14px] font-semibold text-ink-800">{title}</span>
      {desc && <span className="text-[13px] text-ink-400">{desc}</span>}
    </div>
  );
}

export default function RetentionSection() {
  const [range, setRange] = useState(defaultRange);

  const { data, loading, error } = useAnalyticsQuery(
    (p, s) => analyticsApi.overview({ ...p, includeRetention: 1, scope: "customer" }, s),
    [range.from, range.to],
    range
  );
  const { data: topUsers } = useAnalyticsQuery(
    (p, s) => analyticsApi.topUsers({ ...p, metric: "ncUsed", scope: "customer" }, s),
    [range.from, range.to],
    range
  );

  const retention = data?.retention;
  const fu        = retention?.firstUse;
  const blocked   = retention?.blocked;
  const cohorts   = retention?.cohorts || [];
  const maxWeeks  = cohorts.reduce((m, c) => Math.max(m, c.active.length), 0);

  const now = Date.now();
  const idleAll = (topUsers?.users || [])
    .filter((u) => u.lastSeen)
    .map((u) => ({ ...u, idleDays: Math.floor((now - new Date(u.lastSeen).getTime()) / 86400000) }))
    .filter((u) => u.idleDays >= AT_RISK_DAYS);
  const internalExcluded = idleAll.filter((u) => isInternalAccount(u.balance)).length;
  const atRisk = idleAll
    .filter((u) => !isInternalAccount(u.balance))
    .sort((a, b) => b.idleDays - a.idleDays);
  const atRiskWithBalance = atRisk.filter((u) => balanceStatus(u.balance) !== "bad").length;
  const atRiskSoaked      = atRisk.length - atRiskWithBalance;

  return (
    <div className="space-y-5">

      {/* ── 헤더 ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-ink-900">리텐션</h2>
          <span className="text-[13px] text-ink-400">사용자를 계속 붙잡고 있는가</span>
          <InfoTooltip
            intro="가입 → 첫 사용 → 계속 사용 → 이탈, 이 4단계 흐름을 봅니다."
            items={[
              { term: "코호트 표",      desc: "같은 주에 가입한 사람들이 시간이 지나도 계속 쓰는지 (색이 진할수록 잔존율 높음)" },
              { term: "이탈 위험",      desc: `${AT_RISK_DAYS}일 넘게 아무 활동 없는 사용자` },
              { term: "잔액 남김 이탈", desc: "쓸 크레딧이 남았는데 안 옴 → 제품이 안 끌린다는 신호" },
              { term: "소진 후 이탈",   desc: "크레딧을 다 써서 안 옴 → 가격/가치 문제 신호" },
            ]}
          />
        </div>
        <div className="opacity-70 transition-opacity hover:opacity-100">
          <SectionRangePicker from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} />
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[14px] text-red-700">
          데이터를 불러오지 못했습니다 — {error.message}
        </div>
      )}

      {!error && (loading && !data ? <Skeleton h={72} variant="chartTable" /> : (
        <div className="animate-fade-in space-y-5">

          {/* ═══════════════════════════════════════════════════════════════
              STEP 1 — 가입 & 첫 사용
          ═══════════════════════════════════════════════════════════════ */}
          <div>
            <StepLabel title="가입 & 첫 사용" desc="새로 온 사람들이 바로 써보는가" />
            <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
              <KpiCard label="신규 가입" value={fu ? fmt.int(fu.newUsers) : "—"} tone="brand" hero sublabel="이 기간 가입자" />
              <KpiCard label="가입 당일 사용률" tone="good"
                value={fu && fu.newUsers > 0 ? fmt.pct(fu.usedSameDay / fu.newUsers) : "—"}
                sublabel={fu ? `미사용 ${fmt.int(fu.neverUsed)}명 · 첫 사용까지 평균 ${fu.medianDaysToFirstUse ?? "—"}일` : undefined} />
              <KpiCard label="1회 사용 후 이탈" tone="warn"
                value={fu && fu.newUsers > 0 ? fmt.pct(fu.oneAndDone / fu.newUsers) : "—"}
                sublabel={fu ? `${fmt.int(fu.oneAndDone)}명이 한 번 쓰고 다시 안 옴` : undefined} />
              <div className="min-w-0 rounded-xl bg-red-50 p-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)] sm:p-4">
                <div className="flex items-center gap-1.5 text-xs font-medium text-red-700">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
                  이탈 위험
                </div>
                <div className="mt-1.5 text-lg font-semibold tabular-nums text-red-800 sm:text-xl">
                  {topUsers ? `${fmt.int(atRisk.length)}명` : "—"}
                </div>
                <div className="mt-1 text-[11px] text-red-600">
                  {topUsers ? `${AT_RISK_DAYS}일+ 무활동 · 잔액 보유 ${fmt.int(atRiskWithBalance)}명` : undefined}
                </div>
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════════
              STEP 2 — 크레딧 소진 → 결제 전환
          ═══════════════════════════════════════════════════════════════ */}
          <div>
            <StepLabel title="크레딧 소진 후 행동" desc="크레딧이 떨어진 순간, 결제로 이어지는가" />
            {!blocked || blocked.blockedUsers === 0 ? (
              <div className="rounded-xl border border-dashed border-ink-200 bg-ink-50/50 px-5 py-6 text-center">
                <p className="text-[14px] text-ink-500">아직 크레딧이 소진된 사용자가 없어요</p>
                <p className="mt-1 text-[13px] text-ink-400">소진이 발생하면 여기에 충전·복귀 여부가 자동으로 쌓입니다</p>
              </div>
            ) : (
              <KpiRow cols={4}>
                <KpiCard label="크레딧 소진" value={`${fmt.int(blocked.blockedUsers)}명`}
                  sublabel="잔액 부족으로 사용 중단" />
                <KpiCard label="24시간 내 충전" tone="good" hero
                  value={fmt.pct(blocked.chargedWithin24h / blocked.blockedUsers)}
                  sublabel={`${fmt.int(blocked.chargedWithin24h)}명이 당일~익일 결제`} />
                <KpiCard label="나중에 충전 / 무료 복귀"
                  value={`${fmt.int(blocked.chargedLater)} / ${fmt.int(blocked.returnedWithoutCharge)}`}
                  sublabel="하루 이후 충전 / 지급받고 재사용" />
                <KpiCard label="이탈" tone={blocked.neverReturned > 0 ? "warn" : "default"}
                  value={`${fmt.int(blocked.neverReturned)}명`}
                  sublabel="소진 후 충전·사용 기록 없음" />
              </KpiRow>
            )}
          </div>

          {/* ═══════════════════════════════════════════════════════════════
              STEP 3 — 주별 잔존율 (코호트)
          ═══════════════════════════════════════════════════════════════ */}
          <div>
            <StepLabel title="가입 주별 잔존율" desc="가입한 사람들이 몇 주째 계속 쓰는가" />
            <Panel>
              {cohorts.length === 0 ? (
                <div className="rounded-xl border border-dashed border-ink-200 bg-ink-50/50 px-5 py-6 text-center">
                  <p className="text-[14px] text-ink-500">이 기간에 가입한 사용자가 아직 없어요</p>
                  <p className="mt-1 text-[13px] text-ink-400">조회 범위를 넓혀 보세요</p>
                </div>
              ) : (
                <>
                  {/* 히트맵 범례 */}
                  <div className="mb-3 flex items-center gap-2 text-[13px] text-ink-500">
                    <span>색이 진할수록 많이 남아있음:</span>
                    <span className="flex items-center gap-1">
                      {[95, 85, 75, 68].map((l) => (
                        <span key={l} className="inline-block h-4 w-6 rounded" style={{ background: `hsl(158 52% ${l}%)` }} />
                      ))}
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[14px]">
                      <thead>
                        <tr className="border-b border-ink-100">
                          <th className="sticky left-0 z-10 whitespace-nowrap bg-white px-3 py-2.5 text-left text-[13px] font-semibold text-ink-600">가입 주</th>
                          <th className="whitespace-nowrap px-3 py-2.5 text-center text-[13px] font-semibold text-ink-600">인원</th>
                          {Array.from({ length: maxWeeks }, (_, w) => (
                            <th key={w} className="whitespace-nowrap px-3 py-2.5 text-center text-[13px] font-semibold text-ink-600">
                              {w === 0 ? "가입 주" : `+${w}주`}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-ink-50">
                        {cohorts.map((c) => (
                          <tr key={c.week} className="hover:bg-ink-50/40">
                            <td className="sticky left-0 z-10 whitespace-nowrap bg-white px-3 py-3 font-medium text-ink-900">
                              {formatBucketLabel(c.week, "week")}
                            </td>
                            <td className="whitespace-nowrap px-3 py-3 text-center tabular-nums text-ink-500">{c.size}</td>
                            {Array.from({ length: maxWeeks }, (_, w) => {
                              if (w >= c.active.length) {
                                return <td key={w} className="px-3 py-3 text-center text-ink-200">·</td>;
                              }
                              const pct = c.size > 0 ? c.active[w] / c.size : 0;
                              return (
                                <td
                                  key={w}
                                  className="whitespace-nowrap px-3 py-3 text-center tabular-nums text-ink-700"
                                  style={{ backgroundColor: heatColor(pct) }}
                                  title={`${c.active[w]}명 / ${c.size}명`}
                                >
                                  {fmt.pct(pct)}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </Panel>
          </div>

          {/* ═══════════════════════════════════════════════════════════════
              STEP 4 — 이탈 위험 사용자
          ═══════════════════════════════════════════════════════════════ */}
          <div>
            <StepLabel title="이탈 위험 사용자" desc={`${AT_RISK_DAYS}일 넘게 안 들어온 사람들`} />

            {atRisk.length === 0 ? (
              <div className="rounded-xl border border-dashed border-emerald-200 bg-emerald-50/50 px-5 py-6 text-center">
                <p className="text-[14px] text-emerald-700 font-medium">이탈 위험 사용자가 없어요 👍</p>
                <p className="mt-1 text-[13px] text-emerald-600/70">모든 활성 사용자가 최근 {AT_RISK_DAYS}일 내 접속함</p>
              </div>
            ) : (
              <Panel>
                {/* 요약 배너 */}
                <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div className="flex items-center gap-3 rounded-xl bg-red-50 px-4 py-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                      <span className="text-lg font-bold text-red-700">{atRiskWithBalance}</span>
                    </div>
                    <div>
                      <p className="text-[14px] font-semibold text-red-800">잔액 남김 이탈</p>
                      <p className="text-[12px] text-red-600">크레딧 있는데 안 옴 → 제품 문제 가능성</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-xl bg-amber-50 px-4 py-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                      <span className="text-lg font-bold text-amber-700">{atRiskSoaked}</span>
                    </div>
                    <div>
                      <p className="text-[14px] font-semibold text-amber-800">소진 후 이탈</p>
                      <p className="text-[12px] text-amber-600">크레딧 다 써서 안 옴 → 가격/가치 문제</p>
                    </div>
                  </div>
                </div>

                {/* 사용자 목록 */}
                <div className="overflow-x-auto">
                  <table className="w-full table-fixed text-[14px]">
                    <colgroup>
                      <col />
                      <col className="w-20" />
                      <col className="w-24" />
                      <col className="w-28" />
                      <col className="w-28" />
                    </colgroup>
                    <thead>
                      <tr className="border-b border-ink-100 text-[13px] text-ink-500">
                        <th className="pb-2.5 text-left font-medium">이메일</th>
                        <th className="pb-2.5 text-center font-medium">무활동</th>
                        <th className="pb-2.5 text-center font-medium">잔액</th>
                        <th className="pb-2.5 text-center font-medium">진단</th>
                        <th className="pb-2.5 text-center font-medium">이 기간 사용량</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ink-50">
                      {atRisk.map((u) => {
                        const broke = balanceStatus(u.balance) === "bad";
                        return (
                          <tr key={u.userId} className={`hover:bg-ink-50/40 ${!broke ? "bg-red-50/15" : ""}`}>
                            <td className="truncate py-2.5 font-medium text-ink-700">{u.email || u.userId}</td>
                            <td className="whitespace-nowrap py-2.5 text-center tabular-nums font-semibold text-ink-900">{u.idleDays}일</td>
                            <td className="whitespace-nowrap py-2.5 text-center tabular-nums text-ink-600">{fmt.nc(u.balance)}</td>
                            <td className="whitespace-nowrap py-2.5 text-center">
                              <span className={`rounded-full px-2 py-0.5 text-[12px] font-semibold ${
                                broke ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-600"
                              }`}>
                                {broke ? "소진 후 이탈" : "잔액 남김 이탈"}
                              </span>
                            </td>
                            <td className="whitespace-nowrap py-2.5 text-center tabular-nums text-ink-500">{fmt.nc(u.ncUsed)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="mt-3 text-[13px] text-ink-500">
                  조치는 전체 사용자 탭에서 — 이메일 검색 후 지급, 또는 일괄 크레딧 지급.
                  {internalExcluded > 0 && ` (내부/테스트 계정 ${internalExcluded}명은 제외됨)`}
                </p>
              </Panel>
            )}
          </div>

        </div>
      ))}
    </div>
  );
}
